import React, { Suspense } from "react";
import { RouteObject, createBrowserRouter } from "react-router-dom";
import DashboardLayout from "../Layouts/DashboardLayout/DashboardLayout";
import AdminLayout from "../Layouts/AdminLayout/AdminLayout";
import { ROUTES_MAP } from "./routesMap";
import { LogoutPage } from "../Pages/dashboard/logout/index.page";
import { PageSkeleton } from "../Components/PageSkeleton/PageSkeleton";
import { configuredFrontendBasePath } from "./frontendBasePath";

const LiveStreams = React.lazy(
  () => import("../Pages/dashboard/live-streams/index.page")
);
const BlockedRegistrations = React.lazy(() => import('../Pages/dashboard/admin/blocked-registrations/blocked-registrations.page'));
const AbuseIncidents = React.lazy(() => import('../Pages/dashboard/admin/abuse-incidents/abuse-incidents.page'));
const AdminUsers = React.lazy(
  () => import("../Pages/dashboard/admin/users/users.page")
);
const AdminLiveStreams = React.lazy(
  () => import("../Pages/dashboard/admin/live-streams/live-streams.page")
);
const AuthLayout = React.lazy(() => import("../Layouts/AuthLayout/AuthLayout"));
const LoginPage = React.lazy(() => import("../Pages/auth/login/login.page"));
const SignupPage = React.lazy(() => import("../Pages/auth/signup/signup.page"));
const ForgotPasswordPage = React.lazy(
  () => import("../Pages/auth/forgot-password/forgot-password.page")
);
const ResetPasswordPage = React.lazy(
  () => import("../Pages/auth/reset-password/reset-password.page")
);
const Faqs = React.lazy(() => import("../Pages/dashboard/Faqs/Faqs"));
const LiveStreamDetails = React.lazy(
  () => import("../Pages/dashboard/live-streams/[id]/index.page")
);
const RedirectToHome = React.lazy(() => import("../Pages/redirect_to_home"));
const LoginAsUser = React.lazy(() => import("../Pages/login_as_user"));
const CheckoutReturnPage = React.lazy(
  () => import("../Pages/streams/checkout-return.page")
);
const NotFoundPage = React.lazy(
  () => import("../Pages/not-found/not-found.page")
);

const LoaderComponent = () => <PageSkeleton />;

// No entrance animation at Working level (spec §6): pages just appear.
const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<LoaderComponent />}>
    <Component />
  </Suspense>
);

const routes: RouteObject[] = [
  {
    path: "/",
    element: (
      <Suspense fallback={<LoaderComponent />}>
        <RedirectToHome />
      </Suspense>
    ),
  },
  {
    path: ROUTES_MAP.auth.asUser,
    element: (
      <Suspense fallback={<LoaderComponent />}>
        <LoginAsUser />
      </Suspense>
    ),
  },
  {
    path: ROUTES_MAP.auth.index,
    element: withSuspense(AuthLayout),
    children: [
      {
        path: ROUTES_MAP.auth.login,
        element: withSuspense(LoginPage),
      },
      {
        path: ROUTES_MAP.auth.signup,
        element: withSuspense(SignupPage),
      },
      {
        path: ROUTES_MAP.auth.forgotPassword,
        element: withSuspense(ForgotPasswordPage),
      },
      {
        path: ROUTES_MAP.auth.resetPassword,
        element: withSuspense(ResetPasswordPage),
      },
    ],
  },
  {
    path: ROUTES_MAP.checkoutReturn,
    element: (
      <Suspense fallback={<LoaderComponent />}>
        <DashboardLayout />
      </Suspense>
    ),
    children: [{ index: true, element: withSuspense(CheckoutReturnPage) }],
  },
  {
    path: ROUTES_MAP.dashboard.index,
    element: (
      <Suspense fallback={<LoaderComponent />}>
        <DashboardLayout />
      </Suspense>
    ),
    children: [
      {
        path: ROUTES_MAP.dashboard.admin.index,
        element: (
          <Suspense fallback={<LoaderComponent />}>
            <AdminLayout />
          </Suspense>
        ),
        children: [
          {
            path: ROUTES_MAP.dashboard.admin.users,
            element: withSuspense(AdminUsers),
          },
          {
            path: ROUTES_MAP.dashboard.admin.liveStreams,
            element: withSuspense(AdminLiveStreams),
          },
          { path: ROUTES_MAP.dashboard.admin.blockedRegistrations, element: withSuspense(BlockedRegistrations) },
          { path: ROUTES_MAP.dashboard.admin.abuseIncidents, element: withSuspense(AbuseIncidents) },
        ],
      },
      {
        path: ROUTES_MAP.dashboard.liveStreams,
        element: withSuspense(LiveStreams),
      },
      {
        path: ROUTES_MAP.dashboard.liveStreamDetails(":id"),
        element: withSuspense(LiveStreamDetails),
      },
      {
        path: ROUTES_MAP.dashboard.faqs,
        element: withSuspense(Faqs),
      },
      {
        path: ROUTES_MAP.dashboard.logout,
        element: (
          <Suspense fallback={<LoaderComponent />}>
            <LogoutPage />
          </Suspense>
        ),
      },
    ],
  },
  ...(import.meta.env.DEV
    ? [
        {
          path: "/dev/design",
          element: withSuspense(React.lazy(() => import("../Pages/dev/design-preview.page"))),
        },
      ]
    : []),
  {
    path: ROUTES_MAP.notFound,
    element: withSuspense(NotFoundPage),
  },
];

export const router = createBrowserRouter(routes, {
  basename: configuredFrontendBasePath(),
});
